import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiPlus, FiX, FiCheck, FiRefreshCw, FiTrash2, FiGlobe, FiAlertCircle } from "react-icons/fi";
import { useToast } from "../components/ToastProvider";
import { api } from "../utils/api";
import "../styles/Dashboard.css";
import "../styles/Modal.css";

export default function AdvancedDomainsPage() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { addToast } = useToast();

  const fetchDomains = useCallback(async () => {
    try {
      const data = await api.getDomains();
      setDomains(data.domains || []);
    } catch (err) {
      console.error(err);
      addToast("Failed to load domains", "error");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    document.title = "Custom Domains - LinkSquire";
    fetchDomains();
  }, [fetchDomains]);

  const verifyDomain = async (id) => {
    setVerifyingId(id);
    try {
      const data = await api.verifyDomain(id);
      
      if (data.status === 'active') {
        addToast("Domain verified successfully!", "success");
      } else {
        addToast("Domain not yet verified. Check DNS settings.", "warning");
      }
      fetchDomains(); // refresh list
    } catch (err) {
      addToast("Verification check failed", "error");
    } finally {
      setVerifyingId(null);
    }
  };

  const deleteDomain = async (id, domainName) => {
    if (!window.confirm(`Are you sure you want to delete ${domainName}? Links using this domain will stop working.`)) return;
    
    setDeletingId(id);
    try {
      await api.deleteDomain(id);
      addToast("Domain deleted", "success");
      setDomains(domains.filter(d => d.$id !== id));
    } catch (err) {
      addToast("Failed to delete domain", "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Advanced</h1>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          <FiPlus /> Add Domain
        </button>
      </div>

      <div className="advanced-tabs" style={{ display: 'flex', gap: '20px', marginBottom: '24px', borderBottom: '1px solid var(--border)' }}>
        <Link to="/advanced/ab-testing" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>A/B Testing</Link>
        <Link to="/advanced/geo-targeting" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>Geo-Targeting</Link>
        <Link to="/advanced/pixels" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>Retargeting Pixels</Link>
        <Link to="/advanced/domains" style={{ padding: '8px 4px', borderBottom: '2px solid var(--text)', color: 'var(--text)', fontWeight: 500, textDecoration: 'none' }}>Custom Domains</Link>
        <Link to="/advanced/conversions" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>Conversions</Link>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading domains...</p>
        </div>
      ) : domains.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface)' }}>
          <FiGlobe size={40} style={{ color: 'var(--text-secondary)', marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ marginBottom: '8px', fontSize: '16px' }}>No custom domains</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>Build brand trust by using your own domain for your short links.</p>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>Add Custom Domain</button>
        </div>
      ) : (
        <div className="links-section">
          <div className="links-header">
            <h2>Your Custom Domains</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {domains.map(d => (
              <div key={d.$id} style={{ padding: '20px', borderBottom: '1px solid var(--border)', background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {d.domain}
                      {d.status === 'active' 
                        ? <span style={{ fontSize: '11px', padding: '2px 8px', background: '#dcfce7', color: '#0369a1', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}><FiCheck /> Verified</span>
                        : <span style={{ fontSize: '11px', padding: '2px 8px', background: '#fef9c3', color: '#b45309', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}><FiAlertCircle /> Pending Verification</span>
                      }
                    </div>
                    
                    {d.status !== 'active' && (
                      <div style={{ background: 'var(--bg)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)', marginTop: '12px', fontSize: '13px' }}>
                        <p style={{ marginBottom: '12px', fontWeight: 500 }}>To verify your domain, add the following record to your DNS provider:</p>
                        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <th style={{ paddingBottom: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Type</th>
                              <th style={{ paddingBottom: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Name</th>
                              <th style={{ paddingBottom: '8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{ paddingTop: '8px', fontWeight: 600 }}>CNAME</td>
                              <td style={{ paddingTop: '8px', fontFamily: 'monospace' }}>{d.domain.split('.').length > 2 ? d.domain.split('.')[0] : '@'}</td>
                              <td style={{ paddingTop: '8px', fontFamily: 'monospace' }}>cname.vercel-dns.com</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {d.status !== 'active' && (
                      <button 
                        className="btn-sm" 
                        onClick={() => verifyDomain(d.$id)} 
                        data-tooltip="Check verification status"
                        disabled={verifyingId === d.$id}
                      >
                        {verifyingId === d.$id ? <span className="spinner-sm dark" /> : <FiRefreshCw />} Verify
                      </button>
                    )}
                    <button 
                      className="btn-sm btn-sm-danger" 
                      onClick={() => deleteDomain(d.$id, d.domain)} 
                      data-tooltip="Delete domain"
                      disabled={deletingId === d.$id}
                    >
                      {deletingId === d.$id ? <span className="spinner-sm dark" /> : <FiTrash2 />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAddModal && (
        <AddDomainModal 
          onClose={() => setShowAddModal(false)} 
          onAdded={() => {
            setShowAddModal(false);
            fetchDomains();
          }} 
        />
      )}
    </div>
  );
}

function AddDomainModal({ onClose, onAdded }) {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.addDomain(domain.trim().toLowerCase());
      addToast("Domain added successfully", "success");
      onAdded();
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Custom Domain</h2>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Domain Name</label>
            <input 
              type="text" 
              value={domain} 
              onChange={(e) => setDomain(e.target.value)} 
              placeholder="e.g. go.mycompany.com" 
              required 
            />
            <span className="form-hint" style={{ marginTop: '6px' }}>We recommend using a subdomain like <strong>link.yourdomain.com</strong> or <strong>go.yourdomain.com</strong>.</span>
          </div>
          
          <button type="submit" className="btn-primary" disabled={loading || !domain}>
            {loading ? "Adding..." : "Add Domain"}
          </button>
        </form>
      </div>
    </div>
  );
}